import { Popover } from '@pique/component';
import { enhancedNotify } from '@pique/core/components/shared/notification-utils';
import {
  ActionItemsService,
  getPlannedFocusMinutes,
  getPlannedFocusSeconds,
} from '@pique/core/modules/properties/services/action-items-service';
import {
  ACTION_ITEM_STATUS,
  type ActionItemStatus,
  type SerializedTodoItem,
} from '@pique/core/modules/properties/services/schema';
import { posthog } from '@pique/core/posthog';
import { useService } from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { actionItemsHeaderModeAtom } from '../../../../../atoms';

import * as styles from './PomodoroTimer.css';
import { PomodoroTimerDisplay } from './PomodoroTimerDisplay';
import { formatHumanDuration, formatPlannedCompact } from './pomodoro-timer-utils';

interface PomodoroTimerProps {
  item: SerializedTodoItem;
  isInFocusZone: boolean;
  isActiveFocus: boolean;
  focusedSessionStartedAtMs: number | null;
  onTimerActiveChange?: (isActive: boolean) => void;
  onMenuOpenChange?: (open: boolean) => void;
  showTooltips?: boolean;
  menuMode?: 'popover' | 'inline' | 'none';
}

const TIME_MENU_OPTIONS_MINUTES = [15, 30, 45, 60, 90, 120];
const PLANNED_TIME_MENU_OPTIONS_MINUTES = TIME_MENU_OPTIONS_MINUTES.filter(
  minutes => minutes >= 45
);

const formatMenuMinutes = (minutes: number): string => {
  if (minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  }
  return `${minutes} min`;
};

const getTodayKey = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const PomodoroTimer: React.FC<PomodoroTimerProps> = ({
  item,
  isInFocusZone,
  isActiveFocus,
  focusedSessionStartedAtMs,
  onTimerActiveChange,
  onMenuOpenChange,
  showTooltips = false,
  menuMode = 'popover',
}) => {
  const actionItemsService = useService(ActionItemsService);
  const setHeaderMode = useSetAtom(actionItemsHeaderModeAtom);

  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const [actualMenuOpen, setActualMenuOpen] = useState(false);
  const [plannedMenuOpen, setPlannedMenuOpen] = useState(false);
  const actualButtonRef = useRef<HTMLButtonElement | null>(null);
  const plannedButtonRef = useRef<HTMLButtonElement | null>(null);
  const actualMenuRef = useRef<HTMLDivElement | null>(null);
  const plannedMenuRef = useRef<HTMLDivElement | null>(null);

  const plannedMinutes = getPlannedFocusMinutes(item);
  const plannedSeconds = getPlannedFocusSeconds(item);

  const rawFocus = item.focusTimeSeconds;
  const persistedTotalSeconds =
    typeof rawFocus === 'number'
      ? rawFocus
      : typeof rawFocus === 'string'
        ? Number.parseInt(rawFocus, 10) || 0
        : 0;

  const todayKey = getTodayKey();
  const focusDaily = item.focusDaily ?? {};
  const persistedTodaySeconds =
    typeof focusDaily[todayKey] === 'number'
      ? Math.max(0, Math.floor(focusDaily[todayKey] as number))
      : persistedTotalSeconds;

  const [displaySec, setDisplaySec] = useState<number>(() => {
    if (isActiveFocus && typeof focusedSessionStartedAtMs === 'number') {
      return (
        persistedTodaySeconds +
        Math.max(0, Math.floor((Date.now() - focusedSessionStartedAtMs) / 1000))
      );
    }
    return persistedTodaySeconds;
  });
  const isTimerRunning = isActiveFocus && !isTimerPaused;

  useEffect(() => {
    onTimerActiveChange?.(isTimerRunning);
  }, [isTimerRunning, onTimerActiveChange]);

  useEffect(() => {
    return () => {
      onMenuOpenChange?.(false);
    };
  }, [onMenuOpenChange]);

  useEffect(() => {
    if (!isActiveFocus || !isInFocusZone) {
      return;
    }

    if (plannedSeconds <= 0) return;
    if (displaySec < plannedSeconds) return;

    actionItemsService.setFocusedItem(null);
    void enhancedNotify.success(
      {
        title: 'Focus time completed!',
        message: 'Still at it? Drag event in Calendar to add more time',
      },
      { duration: 6000 }
    );
  }, [
    actionItemsService,
    displaySec,
    isActiveFocus,
    isInFocusZone,
    plannedSeconds,
  ]);

  useEffect(() => {
    if (
      !isActiveFocus ||
      typeof focusedSessionStartedAtMs !== 'number' ||
      isTimerPaused
    ) {
      setDisplaySec(persistedTodaySeconds);
      return;
    }

    if (item.status !== ACTION_ITEM_STATUS.IN_PROGRESS && item.id) {
      actionItemsService.updateById(item.id, {
        status: ACTION_ITEM_STATUS.IN_PROGRESS as ActionItemStatus,
      });
    }

    const compute = () =>
      setDisplaySec(
        persistedTodaySeconds +
          Math.max(
            0,
            Math.floor((Date.now() - focusedSessionStartedAtMs) / 1000)
          )
      );
    compute();
    const id = window.setInterval(compute, 1000);
    return () => window.clearInterval(id);
  }, [
    isActiveFocus,
    focusedSessionStartedAtMs,
    persistedTodaySeconds,
    isTimerPaused,
    actionItemsService,
    item.id,
    item.status,
  ]);

  useEffect(() => {
    if (!isActiveFocus && isTimerPaused) {
      setIsTimerPaused(false);
    }
  }, [isActiveFocus, isTimerPaused]);

  const handleTimerClick = useCallback(() => {
    if (!item.id) return;

    const itemTitle =
      typeof item.title === 'string' ? item.title : String(item.title || '');

    if (!isActiveFocus) {
      setIsTimerPaused(false);
      actionItemsService.startFocusSession(item.id);
      setHeaderMode('chart');
      posthog.capture('focus_timer_started', {
        item_id: item.id,
        item_title: itemTitle,
        accumulated_seconds: persistedTodaySeconds,
      });
      window.dispatchEvent(new CustomEvent('pique-focus-timer-started'));
      return;
    }

    if (!isTimerPaused) {
      setIsTimerPaused(true);
      actionItemsService.setFocusedItem(null);
      const sessionSeconds =
        typeof focusedSessionStartedAtMs === 'number'
          ? Math.floor((Date.now() - focusedSessionStartedAtMs) / 1000)
          : 0;
      posthog.capture('focus_timer_paused', {
        item_id: item.id,
        item_title: itemTitle,
        session_seconds: sessionSeconds,
        total_seconds: persistedTodaySeconds + sessionSeconds,
      });
    } else {
      setIsTimerPaused(false);
      actionItemsService.setFocusedItem(item.id);
      posthog.capture('focus_timer_started', {
        item_id: item.id,
        item_title: itemTitle,
        accumulated_seconds: persistedTodaySeconds,
      });
    }
  }, [
    actionItemsService,
    focusedSessionStartedAtMs,
    isActiveFocus,
    isTimerPaused,
    item.id,
    item.title,
    persistedTodaySeconds,
    setHeaderMode,
  ]);

  const notifyMenuOpenChange = useCallback(
    (nextActualOpen: boolean, nextPlannedOpen: boolean) => {
      onMenuOpenChange?.(nextActualOpen || nextPlannedOpen);
    },
    [onMenuOpenChange]
  );

  useEffect(() => {
    if (!actualMenuOpen && !plannedMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;

      if (
        actualButtonRef.current?.contains(target) ||
        plannedButtonRef.current?.contains(target) ||
        actualMenuRef.current?.contains(target) ||
        plannedMenuRef.current?.contains(target)
      ) {
        return;
      }

      setActualMenuOpen(false);
      setPlannedMenuOpen(false);
      notifyMenuOpenChange(false, false);
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [actualMenuOpen, notifyMenuOpenChange, plannedMenuOpen]);

  const handleActualOpenChange = useCallback(
    (nextOpen: boolean) => {
      setActualMenuOpen(nextOpen);
      if (nextOpen) {
        setPlannedMenuOpen(false);
        notifyMenuOpenChange(true, false);
      } else {
        notifyMenuOpenChange(false, plannedMenuOpen);
      }
    },
    [notifyMenuOpenChange, plannedMenuOpen]
  );

  const handlePlannedOpenChange = useCallback(
    (nextOpen: boolean) => {
      setPlannedMenuOpen(nextOpen);
      if (nextOpen) {
        setActualMenuOpen(false);
        notifyMenuOpenChange(false, true);
      } else {
        notifyMenuOpenChange(actualMenuOpen, false);
      }
    },
    [actualMenuOpen, notifyMenuOpenChange]
  );

  const toggleActualMenu = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setPlannedMenuOpen(false);
      setActualMenuOpen(prev => {
        const nextOpen = !prev;
        notifyMenuOpenChange(nextOpen, false);
        return nextOpen;
      });
    },
    [notifyMenuOpenChange]
  );

  const togglePlannedMenu = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setActualMenuOpen(false);
      setPlannedMenuOpen(prev => {
        const nextOpen = !prev;
        notifyMenuOpenChange(false, nextOpen);
        return nextOpen;
      });
    },
    [notifyMenuOpenChange]
  );

  const stopMenuPointerDown = useCallback((event: React.PointerEvent) => {
    event.stopPropagation();
  }, []);

  const stopMenuClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const updateActualSeconds = useCallback(
    (nextSeconds: number) => {
      if (!item.id) return;
      const safeSeconds = Math.max(0, Math.floor(nextSeconds));
      const prevDaily =
        typeof focusDaily[todayKey] === 'number'
          ? Math.max(0, Math.floor(focusDaily[todayKey] as number))
          : persistedTotalSeconds;
      const nextTotal = Math.max(
        0,
        persistedTotalSeconds - prevDaily + safeSeconds
      );
      const nextDaily = {
        ...focusDaily,
        [todayKey]: safeSeconds,
      };
      actionItemsService.updateById(item.id, {
        focusTimeSeconds: nextTotal,
        focusDaily: nextDaily,
      });
    },
    [actionItemsService, focusDaily, item.id, persistedTotalSeconds, todayKey]
  );

  const handleActualSelect = useCallback(
    (minutes: number) => {
      updateActualSeconds(minutes * 60);
      setActualMenuOpen(false);
      notifyMenuOpenChange(false, false);
    },
    [notifyMenuOpenChange, updateActualSeconds]
  );

  const handlePlannedSelect = useCallback(
    (minutes: number) => {
      if (!item.id) return;
      const safeMinutes = Math.max(1, Math.round(minutes));
      actionItemsService.updateById(item.id, {
        plannedDuration: safeMinutes,
      });
      setPlannedMenuOpen(false);
      notifyMenuOpenChange(false, false);
    },
    [actionItemsService, item.id, notifyMenuOpenChange]
  );

  const handleClearActual = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      updateActualSeconds(0);
      setActualMenuOpen(false);
      notifyMenuOpenChange(false, false);
    },
    [notifyMenuOpenChange, updateActualSeconds]
  );

  const actualMenuContent = (
    <div
      className={styles.timerMenu}
      onClick={stopMenuClick}
      onPointerDown={stopMenuPointerDown}
    >
      <div className={styles.timerMenuHeader}>
        <span className={styles.timerMenuLabel}>Current</span>
        <span className={styles.timerMenuValue}>
          {formatHumanDuration(displaySec)}
        </span>
      </div>
      <div className={styles.timerMenuList}>
        {PLANNED_TIME_MENU_OPTIONS_MINUTES.map(minutes => (
          <button
            key={minutes}
            type="button"
            className={styles.timerMenuItem}
            onPointerDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handleActualSelect(minutes);
            }}
            onClick={stopMenuClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handleActualSelect(minutes);
              }
            }}
          >
            {formatMenuMinutes(minutes)}
          </button>
        ))}
      </div>
      <div className={styles.timerMenuDivider} />
      <button
        type="button"
        className={styles.timerMenuClearButton}
        onClick={handleClearActual}
      >
        Clear current
      </button>
    </div>
  );

  const plannedMenuContent = (
    <div
      className={styles.timerMenu}
      onClick={stopMenuClick}
      onPointerDown={stopMenuPointerDown}
    >
      <div className={styles.timerMenuHeader}>
        <span className={styles.timerMenuLabel}>Planned</span>
        <span className={styles.timerMenuValue}>
          {formatPlannedCompact(plannedMinutes)}
        </span>
      </div>
      <div className={styles.timerMenuList}>
        {TIME_MENU_OPTIONS_MINUTES.map(minutes => (
          <button
            key={minutes}
            type="button"
            className={styles.timerMenuItem}
            onPointerDown={e => {
              e.stopPropagation();
              e.preventDefault();
              handlePlannedSelect(minutes);
            }}
            onClick={stopMenuClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                e.preventDefault();
                handlePlannedSelect(minutes);
              }
            }}
          >
            {formatMenuMinutes(minutes)}
          </button>
        ))}
      </div>
    </div>
  );

  const getPortalMenuStyle = useCallback(
    (anchor: HTMLButtonElement | null): React.CSSProperties | undefined => {
      if (!anchor) return undefined;
      const rect = anchor.getBoundingClientRect();
      const preferredWidth = 220;
      const left = Math.max(8, rect.right - preferredWidth);
      const maxTop = window.innerHeight - 8;
      return {
        position: 'fixed',
        top: Math.min(maxTop, rect.top - 6),
        left,
        transform: 'translateY(-100%)',
        zIndex: 2000,
      };
    },
    []
  );

  return (
    <>
      <PomodoroTimerDisplay
        isRunning={isTimerRunning}
        elapsedLabel={formatHumanDuration(displaySec)}
        plannedLabel={formatPlannedCompact(plannedMinutes)}
        menuOpen={menuMode !== 'none' ? actualMenuOpen || plannedMenuOpen : false}
        isOverPlan={plannedSeconds > 0 && displaySec > plannedSeconds}
        onToggle={handleTimerClick}
        showTooltips={showTooltips}
        renderActual={
          menuMode === 'popover'
            ? node => (
                <Popover
                  open={actualMenuOpen}
                  onOpenChange={handleActualOpenChange}
                  content={actualMenuContent}
                  contentOptions={{
                    className: styles.timerMenuContent,
                    side: 'bottom',
                    align: 'start',
                    sideOffset: 6,
                    avoidCollisions: true,
                    collisionPadding: 12,
                  }}
                >
                  <button
                    type="button"
                    className={styles.timerValueButton}
                    onClick={toggleActualMenu}
                    aria-label="Adjust current focus time"
                    title="Adjust current focus time"
                    data-tauri-drag-region="false"
                  >
                    {node}
                  </button>
                </Popover>
              )
            : menuMode === 'inline'
              ? node => (
                  <button
                    ref={actualButtonRef}
                    type="button"
                    className={styles.timerValueButton}
                    onClick={toggleActualMenu}
                    aria-label="Adjust current focus time"
                    title="Adjust current focus time"
                    data-tauri-drag-region="false"
                  >
                    {node}
                  </button>
                )
            : undefined
        }
        renderPlanned={
          menuMode === 'popover'
            ? node => (
                <Popover
                  open={plannedMenuOpen}
                  onOpenChange={handlePlannedOpenChange}
                  content={plannedMenuContent}
                  contentOptions={{
                    className: styles.timerMenuContent,
                    side: 'bottom',
                    align: 'end',
                    sideOffset: 6,
                    avoidCollisions: true,
                    collisionPadding: 12,
                  }}
                >
                  <button
                    type="button"
                    className={styles.timerValueButton}
                    onClick={togglePlannedMenu}
                    aria-label="Adjust planned focus time"
                    title="Adjust planned focus time"
                    data-tauri-drag-region="false"
                  >
                    {node}
                  </button>
                </Popover>
              )
            : menuMode === 'inline'
              ? node => (
                  <button
                    ref={plannedButtonRef}
                    type="button"
                    className={styles.timerValueButton}
                    onClick={togglePlannedMenu}
                    aria-label="Adjust planned focus time"
                    title="Adjust planned focus time"
                    data-tauri-drag-region="false"
                  >
                    {node}
                  </button>
                )
            : undefined
        }
      />
      {menuMode === 'inline' && actualMenuOpen
        ? createPortal(
            <div
              ref={actualMenuRef}
              className={styles.portalMenuContent}
              style={getPortalMenuStyle(actualButtonRef.current)}
              onClick={stopMenuClick}
              onPointerDown={stopMenuPointerDown}
            >
              {actualMenuContent}
            </div>,
            document.body
          )
        : null}
      {menuMode === 'inline' && plannedMenuOpen
        ? createPortal(
            <div
              ref={plannedMenuRef}
              className={styles.portalMenuContent}
              style={getPortalMenuStyle(plannedButtonRef.current)}
              onClick={stopMenuClick}
              onPointerDown={stopMenuPointerDown}
            >
              {plannedMenuContent}
            </div>,
            document.body
          )
        : null}
    </>
  );
};
